import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export interface IoCRecord {
    id: string;
    type: string;
    value: string;
    severity: number;
    sourceCount: number;
    killchainPhase?: string | null;
    firstSeen: string;
    lastSeen: string;
    tags: string[];
    metadata: Record<string, unknown>;
    /**
     * First-seen snapshot of fields the source produced. Used by the detail
     * dialog to render an "overridden vs. source" diff and to revert a field
     * back to its source value. Undefined keys mean no snapshot was captured
     * for that field yet.
     */
    sourceValues?: {
        severity?: number;
        tags?: string[];
        killchain_phase?: string | null;
    };
}

/** Lockheed Martin Cyber Kill Chain phases — matches iocs.killchain_phase. */
export const KILLCHAIN_PHASES = [
    { slug: "reconnaissance", label: "Reconnaissance" },
    { slug: "weaponization", label: "Weaponization" },
    { slug: "delivery", label: "Delivery" },
    { slug: "exploitation", label: "Exploitation" },
    { slug: "installation", label: "Installation" },
    { slug: "command_and_control", label: "Command & Control" },
    { slug: "actions_on_objectives", label: "Actions on Objectives" },
] as const;

export type KillchainPhase = typeof KILLCHAIN_PHASES[number]["slug"];

export interface IoCListResult {
    iocs: IoCRecord[];
    total: number;
    limit: number;
    offset: number;
}

export interface IoCDetail {
    ioc: IoCRecord;
    sources: string[];
}

export interface IoCSighting {
    id: number;
    sourceSlug: string;
    seenAt: string;
    raw: Record<string, unknown>;
}

export interface FeedSourceRecord {
    slug: string;
    name: string;
    kind: string;
    enabled: boolean;
    scheduleSeconds: number;
    weight: number;
    lastRunAt?: string;
    lastStatus?: string;
    lastError: string;
    lastIngestedCount: number;
    lastRowsFetched: number;
    ingestedCount: number;
}

export interface IoCStatsBucket {
    key: string;
    count: number;
}

export interface IoCStats {
    total: number;
    sightings1h: number;
    sightings24h: number;
    byType: IoCStatsBucket[] | null;
    bySource: IoCStatsBucket[] | null;
    bySeverity: IoCStatsBucket[] | null;
}

export interface IoCFilters {
    types?: string[]; // multi-select
    search?: string;
    minSeverity?: number;
    source?: string;
    killchain?: KillchainPhase | "unclassified";
    limit?: number;
    offset?: number;
}

const LIST_KEY = (f: IoCFilters) =>
    [
        "admin",
        "iocs",
        (f.types ?? []).join(","),
        f.search ?? "",
        f.minSeverity ?? 0,
        f.source ?? "",
        f.killchain ?? "",
        f.limit ?? 50,
        f.offset ?? 0,
    ] as const;

export function useIoCs(filters: IoCFilters = {}) {
    const limit = filters.limit ?? 50;
    const offset = filters.offset ?? 0;
    return useQuery({
        queryKey: LIST_KEY(filters),
        queryFn: () => {
            const p = new URLSearchParams();
            if (filters.types && filters.types.length > 0) p.set("type", filters.types.join(","));
            if (filters.search) p.set("search", filters.search);
            if (filters.minSeverity && filters.minSeverity > 1) p.set("minSeverity", String(filters.minSeverity));
            if (filters.source) p.set("source", filters.source);
            if (filters.killchain) p.set("killchain", filters.killchain);
            p.set("limit", String(limit));
            p.set("offset", String(offset));
            return api.get<IoCListResult>(`/api/v2/iocs?${p.toString()}`);
        },
        staleTime: 5_000,
    });
}

export function useIoCStats() {
    return useQuery({
        queryKey: ["admin", "iocs", "stats"] as const,
        queryFn: () => api.get<IoCStats>("/api/v2/iocs/stats"),
        staleTime: 10_000,
        refetchInterval: 30_000,
    });
}

export function useIoC(id: string | null) {
    return useQuery({
        queryKey: ["admin", "iocs", "detail", id] as const,
        queryFn: () => api.get<IoCDetail>(`/api/v2/iocs/${id}`),
        enabled: !!id,
        staleTime: 5_000,
    });
}

// Verdict envelope (?envelope=v2). Used by the IoC detail dialog's
// source roll-up to pull the per-source fidelity / weight / first/last
// seen + evidence URL — the legacy `sources: string[]` shape doesn't
// carry those fields. Schema doc:
// docs/architecture/verdict-response-schema-v1.md
export interface VerdictSourceEntry {
    id: string;
    fidelity: "low" | "med" | "high";
    first_seen: string;
    last_seen: string;
    evidence_url: string;
    category: string;
    weight: number;
    is_national_cert: boolean;
}

export interface VerdictProvenance {
    first_seen_in_wild: string | null;
    last_seen_in_wild: string | null;
    first_ingested: string | null;
    observation_count: number;
    sources: VerdictSourceEntry[];
    truncated_count: number;
}

export interface VerdictEnvelope {
    indicator: { type: string; value: string };
    provenance: VerdictProvenance;
    // …other top-level keys exist (verdict, identity, behaviour,
    // narrative, pivots, ttl_hint) — only the shape needed by the
    // source-rollup table is declared here. The dialog can extend
    // this interface as more pieces of the envelope make their way
    // into the operator UI.
}

export function useIoCEnvelope(id: string | null) {
    return useQuery({
        queryKey: ["admin", "iocs", "envelope", id] as const,
        queryFn: () => api.get<VerdictEnvelope>(`/api/v2/iocs/${id}?envelope=v2`),
        enabled: !!id,
        staleTime: 10_000,
    });
}

export function useIoCSightings(id: string | null) {
    return useQuery({
        queryKey: ["admin", "iocs", "sightings", id] as const,
        queryFn: () =>
            api
                .get<{ sightings: IoCSighting[] }>(`/api/v2/iocs/${id}/sightings?limit=50`)
                .then((d) => d.sightings),
        enabled: !!id,
        staleTime: 10_000,
    });
}

interface UpdateIoCInput {
    id: string;
    severity?: number;
    tags?: string[];
    /** "" clears the override; valid phase strings set it. */
    killchainPhase?: string;
}

export function useUpdateIoC() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...patch }: UpdateIoCInput) =>
            api.patch<IoCDetail>(`/api/v2/iocs/${id}`, patch),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: ["admin", "iocs"] });
            void qc.invalidateQueries({ queryKey: ["admin", "iocs", "detail", variables.id] });
        },
    });
}

interface RevertIoCInput {
    id: string;
    /** subset of "severity" | "tags" | "killchain_phase" */
    fields: string[];
}

/** Resets the named fields to the source-captured values and clears any
 * overridden flags (severity only today). The server returns the refreshed
 * IoCDetail payload. */
export function useRevertIoC() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, fields }: RevertIoCInput) =>
            api.post<IoCDetail>(`/api/v2/iocs/${id}/revert`, { fields }),
        onSuccess: (_data, variables) => {
            void qc.invalidateQueries({ queryKey: ["admin", "iocs"] });
            void qc.invalidateQueries({ queryKey: ["admin", "iocs", "detail", variables.id] });
        },
    });
}

/** Shape accepted by POST /api/v2/iocs/bulk. */
export interface BulkIoCInput {
    action: "add_tags" | "remove_tags" | "set_severity" | "set_killchain" | "delete";
    iocIds?: string[];
    filters?: {
        types?: string[];
        search?: string;
        source?: string;
        minSeverity?: number;
        killchain?: string;
    };
    tags?: string[];
    severity?: number;
    killchainPhase?: string; // "" clears
    confirmLarge?: boolean;
}

export interface BulkIoCResult {
    affected: number;
    targetCount: number;
}

export const BULK_THRESHOLD = 500;

export function useBulkIoC() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: BulkIoCInput) => api.post<BulkIoCResult>("/api/v2/iocs/bulk", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "iocs"] });
        },
    });
}

export function useFeedSources() {
    return useQuery({
        queryKey: ["admin", "feed-sources"] as const,
        queryFn: () =>
            api
                .get<{ feedSources: FeedSourceRecord[] }>("/api/v2/feed-sources")
                .then((d) => d.feedSources),
        staleTime: 10_000,
        refetchInterval: 15_000,
    });
}

// ---------- Saved IoC filter views (Phase 1D follow-up) ----------

export interface IoCSavedView {
    id: string;
    userId: string;
    ownerName?: string;
    name: string;
    filter: Record<string, unknown>;
    isShared: boolean;
    isMine: boolean;
    createdAt: string;
    updatedAt: string;
}

export function useIoCSavedViews() {
    return useQuery({
        queryKey: ["admin", "iocs", "saved-views"] as const,
        queryFn: () =>
            api
                .get<{ views: IoCSavedView[] }>("/api/v2/iocs/saved-views")
                .then((d) => d.views),
        staleTime: 30_000,
    });
}

export interface CreateIoCSavedViewInput {
    name: string;
    filter: Record<string, unknown>;
    isShared?: boolean;
}

export function useCreateIoCSavedView() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateIoCSavedViewInput) =>
            api.post<{ view: IoCSavedView }>("/api/v2/iocs/saved-views", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "iocs", "saved-views"] });
        },
    });
}

export function useDeleteIoCSavedView() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            api.delete<void>(`/api/v2/iocs/saved-views/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "iocs", "saved-views"] });
        },
    });
}

interface UpdateFeedSourceInput {
    slug: string;
    weight?: number;
    enabled?: boolean;
    scheduleSeconds?: number;
}

export function useUpdateFeedSource() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ slug, ...patch }: UpdateFeedSourceInput) =>
            api.patch<void>(`/api/v2/feed-sources/${slug}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["admin", "feed-sources"] });
        },
    });
}
